// Primero de todo: configura zod antes de que se cargue ningún schema (ver el módulo).
import '#lib/zod-runtime.js';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { AuthProvider } from '#features/auth/auth-context';
import { HostGate } from '#features/auth/vitto/host-gate';
import { authSession } from '#lib/auth/auth-session';
import { bootVittoSession } from '#lib/auth/vitto-boot';
import { restoreEmbeddedRoute, reportEmbeddedRoutes } from '#lib/host/embedded-route';
import { hostFor } from '#lib/host/host';
import { hostMode } from '#lib/host/mode';
import { HotkeysProvider } from '#lib/hotkeys/hotkeys-context';
import { createQueryClient } from '#lib/query-client';
import { parseSearch, stringifySearch } from '#lib/search-params/serializer';
import { routeTree } from './routeTree.gen';
import './styles.css';

// before anything renders: a standalone document gets back who its Vitto session is (see `bootVittoSession`)
bootVittoSession(hostMode.kind, authSession, globalThis.localStorage ?? null);

// EMBED-ROUTE: a document framed by Vitto comes back to where the person was (the host hands it back as `?path=`)
restoreEmbeddedRoute(window, hostMode);

// Serializador propio: las listas viajan separadas por coma y la URL sigue siendo legible.
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  parseSearch,
  stringifySearch,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// EMBED-ROUTE: …and tells the host where it is, so the NEXT mount can do the same
if (hostMode.kind === 'embedded') {
  const host = hostFor(hostMode);
  if (host) reportEmbeddedRoutes(window, router, host);
}

const root = document.getElementById('root');
if (!root) throw new Error('#root does not exist');
createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={createQueryClient()}>
      <AuthProvider>
        <HostGate>
          <HotkeysProvider>
            <RouterProvider router={router} />
          </HotkeysProvider>
        </HostGate>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
);
